import { Cards } from './cards.js';
let CardTilt = {};


CardTilt = (function () {
  'use strict';

  const DEFAULT_MAX_TILT = 10;
  const REDUCE_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  const cardStates = new WeakMap();
  let prefersReducedMotion = false;

  function parseNumber(value, fallback) {
    const parsed = parseFloat(String(value || '').trim());
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function updateReducedMotionPreference() {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    prefersReducedMotion = window.matchMedia(REDUCE_MOTION_QUERY).matches;
  }

  function shouldSkip(card) {
    if (!card || prefersReducedMotion) return true;
    // The hand hover controller owns both selection and 3D tilt after its
    // painted-card delay. Generic pointer tilt on a resting fanned card changes
    // its hit geometry during that delay, which can hand ownership to a
    // neighboring card without any pointer movement.
    if (card.closest?.('#player-hand')) return true;
    const flag = String(card.dataset.tilt || '').toLowerCase();
    if (flag === 'false' || flag === 'off' || flag === '0') return true;
    if (card.classList.contains('card-tilt-disabled')) return true;
    return false;
  }

  function readConfig(card) {
    if (!card || typeof window === 'undefined' || !window.getComputedStyle) {
      return { maxTilt: DEFAULT_MAX_TILT };
    }

    const styles = window.getComputedStyle(card);
    return {
      maxTilt: parseNumber(styles.getPropertyValue('--card-tilt-max'), DEFAULT_MAX_TILT)
    };
  }

  function setNeutral(card) {
    if (!card) return;
    card.classList.remove('card-tilt-active');
    card.style.setProperty('--card-tilt-x', '0.5');
    card.style.setProperty('--card-tilt-y', '0.5');
    card.style.setProperty('--card-tilt-rotate-x', '0deg');
    card.style.setProperty('--card-tilt-rotate-y', '0deg');
    card.style.setProperty('--card-tilt-opacity', '0');
  }

  function ensureGlare(card) {
    if (!card || card.querySelector('.card-glare')) return;
    const glare = document.createElement('div');
    glare.className = 'card-glare';
    card.prepend(glare);
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  /**
   * Is this pointer sample on the card? Asked only for a TRANSPARENT card,
   * whose listening surface (the document) is far larger than the card: a
   * touch landing anywhere else on the page is somebody else's gesture.
   */
  function withinSurface(state, event) {
    const surface = state?.surface;
    if (!surface || typeof surface.getBoundingClientRect !== 'function') return false;
    const rect = surface.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;
    return event.clientX >= rect.left && event.clientX <= rect.right
      && event.clientY >= rect.top && event.clientY <= rect.bottom;
  }

  function applyTilt(state, event) {
    if (!state || !event) return;
    const card = state.card;
    if (!card || card.classList.contains('dragging')) return;

    const pointerSurface = state.surface || card;
    // An engine that is moving the card names the box the lean is for (the
    // one the card is heading to), so the light does not chase every
    // intermediate frame; otherwise the surface's own box is the truth.
    const rect = state.aimBox
      || (typeof pointerSurface.getBoundingClientRect === 'function'
        ? pointerSurface.getBoundingClientRect()
        : { left: 0, top: 0, width: pointerSurface.offsetWidth || 1, height: pointerSurface.offsetHeight || 1 });
    const width = rect.width || pointerSurface.offsetWidth || 1;
    const height = rect.height || pointerSurface.offsetHeight || 1;
    if (!width || !height) return;

    const x = clamp((event.clientX - rect.left) / width, 0, 1);
    const y = clamp((event.clientY - rect.top) / height, 0, 1);
    const maxTilt = state.config.maxTilt;
    // An engine easing the card in or out names how much of the lean is
    // there yet; the light itself is always where the pointer is.
    const blend = Number.isFinite(state.aimBlend) ? clamp(state.aimBlend, 0, 1) : 1;
    const rotateX = (0.5 - y) * maxTilt * 2 * blend;
    const rotateY = (x - 0.5) * maxTilt * 2 * blend;

    card.style.setProperty('--card-tilt-x', x.toFixed(3));
    card.style.setProperty('--card-tilt-y', y.toFixed(3));
    card.style.setProperty('--card-tilt-rotate-x', `${rotateX.toFixed(2)}deg`);
    card.style.setProperty('--card-tilt-rotate-y', `${rotateY.toFixed(2)}deg`);
  }

  function scheduleUpdate(state, event) {
    if (!state) return;
    state.pendingEvent = event;
    if (state.rafId != null) return;

    const schedule = typeof window !== 'undefined' && window.requestAnimationFrame
      ? window.requestAnimationFrame
      : (cb) => setTimeout(cb, 0);

    state.rafId = schedule(() => {
      state.rafId = null;
      if (!state.pendingEvent) return;
      const pending = state.pendingEvent;
      state.pendingEvent = null;
      if (!state.active) return;
      applyTilt(state, pending);
    });
  }

  function isTouchEvent(event) {
    return event && event.pointerType === 'touch';
  }

  function attach(host, card, options = {}) {
    if (!host || !card || shouldSkip(card)) return;
    const config = readConfig(card);
    /* A TRANSPARENT card is `pointer-events: none` on purpose and can never
       hear its own finger, so it listens on the document and its own box says
       which touches are its own (see registerTransparentPreview). */
    const transparent = options.transparent === true;
    const state = {
      host,
      card,
      config,
      // The box the pointer is measured against: the host's — or the card's
      // own, when the host is only the surface that hears the finger.
      surface: transparent ? card : host,
      transparent,
      active: false,
      touchPointerId: null,
      rafId: null,
      pendingEvent: null,
      leaveTimer: null,
      handlers: null,
      aimBox: null,
      aimBlend: 1,
    };
    const activate = (event) => {
      if (card.classList.contains('dragging')) return;
      // Skip tilt during drop settle — board re-render fires pointerenter
      // on newly created cards under the cursor
      if (document.body.classList.contains('drop-settling')) return;
      // Cancel any pending leave — prevents flicker at edges where 3D
      // transform shifts geometry causing rapid enter/leave cycles
      if (state.leaveTimer != null) {
        clearTimeout(state.leaveTimer);
        state.leaveTimer = null;
      }
      // Best-effort: freeze any in-flight raster swap while the card is
      // tilted. Tilt only rotates the card root and never selects pixels,
      // so a failed lock must not delay or suppress activation.
      try {
        Cards?.lockCardRaster?.(card, 'hover');
      } catch (_) { /* presentation is never hostage to raster state */ }
      state.active = true;
      card.classList.add('card-tilt-active');
      card.style.setProperty('--card-tilt-opacity', '1');
      scheduleUpdate(state, event);
    };

    const onEnter = (event) => {
      // A transparent card hears the whole document: a mouse entering or
      // roaming the page is not on it, so only a touch can lean it.
      if (isTouchEvent(event) || transparent) return;
      activate(event);
    };

    const onDown = (event) => {
      if (!isTouchEvent(event) || state.touchPointerId != null || event.isPrimary === false) return;
      if (transparent) {
        // A popup somebody removed without unregistering must not go on
        // answering the page's touches from a detached node.
        if (!card.isConnected) {
          unregister(card);
          return;
        }
        if (!withinSurface(state, event)) return;
      }
      state.touchPointerId = event.pointerId;
      activate(event);
    };

    const onMove = (event) => {
      if (isTouchEvent(event)) {
        if (state.touchPointerId !== event.pointerId || !state.active) return;
        scheduleUpdate(state, event);
        return;
      }
      if (transparent) return;
      if (!state.active) {
        activate(event);
        return;
      }
      scheduleUpdate(state, event);
    };

    const doLeave = () => {
      state.leaveTimer = null;
      state.active = false;
      if (state.rafId != null && window.cancelAnimationFrame) {
        window.cancelAnimationFrame(state.rafId);
      }
      state.rafId = null;
      state.pendingEvent = null;
      if (state.aimBox) {
        /* AN ENGINE HOLDS THE CARD: it fades the lean out on its own clock,
           together with the size, and calls aim(card, null) when the card is
           at rest — a snap to neutral here would be the one abrupt movement in
           an otherwise continuous shrink. The light goes out now: a highlight
           with no pointer near it is what made a held card look wrong. */
        card.classList.remove('card-tilt-active');
        card.style.setProperty('--card-tilt-opacity', '0');
        return;
      }
      /* SNAP BACK TO NEUTRAL. A hold-the-last-pose option was built for the
         invite ticket and removed again: keeping the angle also kept the GLARE
         lit with no pointer near it, and a highlight with no light source is
         what made a held card look wrong rather than poised. Light exists only
         while the card is being touched. */
      setNeutral(card);
    };

    const finishTouch = (event) => {
      if (!isTouchEvent(event) || state.touchPointerId !== event.pointerId) return;
      state.touchPointerId = null;
      doLeave();
    };

    /* A transparent card hears the WHOLE document, so a pointerleave or a
       lostpointercapture it hears is some element's boundary, not the finger
       lifting: moving the finger's capture (CardDrag, for a press on the
       enlarged card) fires both, and the lean read them as the lift, went flat
       and ignored the finger from then on. Only the finger's own pointerup or
       pointercancel ends a transparent card's touch. */
    const onLostCapture = (event) => {
      if (!transparent) finishTouch(event);
    };

    const onLeave = (event) => {
      if (isTouchEvent(event)) {
        if (!transparent) finishTouch(event);
        return;
      }
      if (transparent) return;
      if (!state.active) {
        doLeave();
        return;
      }
      // Brief delay to absorb re-enter events caused by 3D transform
      // shifting the element boundary at edges/corners
      if (state.leaveTimer != null) clearTimeout(state.leaveTimer);
      state.leaveTimer = setTimeout(doLeave, 30);
    };

    state.handlers = { onEnter, onDown, onMove, onLeave, finishTouch, onLostCapture };

    ensureGlare(card);
    /* CAPTURE for a transparent card. Its samples arrive at the document
       from whatever the finger really landed on, and the board owns that
       pointer: a handler between the target and the document that stops
       propagation would take the light out mid-gesture. (The magnifier's own
       capture listener, registered earlier, still withholds the moves of a
       pinch from this one — a two-finger hold is not a lean.) */
    const phase = transparent ? { capture: true } : false;
    host.addEventListener('pointerenter', onEnter, phase);
    host.addEventListener('pointerdown', onDown, phase);
    host.addEventListener('pointermove', onMove, phase);
    host.addEventListener('pointerleave', onLeave, phase);
    host.addEventListener('pointerup', finishTouch, phase);
    host.addEventListener('pointercancel', finishTouch, phase);
    host.addEventListener('lostpointercapture', onLostCapture, phase);
    host.addEventListener('dragstart', onLeave, phase);

    cardStates.set(card, state);
    setNeutral(card);
  }

  function detach(state) {
    if (!state || !state.host || !state.handlers) return;
    const { host, handlers } = state;
    const phase = state.transparent ? { capture: true } : false;
    host.removeEventListener('pointerenter', handlers.onEnter, phase);
    host.removeEventListener('pointerdown', handlers.onDown, phase);
    host.removeEventListener('pointermove', handlers.onMove, phase);
    host.removeEventListener('pointerleave', handlers.onLeave, phase);
    host.removeEventListener('pointerup', handlers.finishTouch, phase);
    host.removeEventListener('pointercancel', handlers.finishTouch, phase);
    host.removeEventListener('lostpointercapture', handlers.onLostCapture, phase);
    host.removeEventListener('dragstart', handlers.onLeave, phase);
    if (state.rafId != null && window.cancelAnimationFrame) {
      window.cancelAnimationFrame(state.rafId);
    }
    if (state.leaveTimer != null) clearTimeout(state.leaveTimer);
    state.active = false;
    state.rafId = null;
    state.pendingEvent = null;
    state.leaveTimer = null;
    state.touchPointerId = null;
    setNeutral(state.card);
  }

  /**
   * Aim the light for a pointer the host never heard move. An engine that
   * enlarges a card in place moves the card UNDER a stationary pointer, and no
   * browser sends a pointermove for an element's own motion — so the lean was
   * computed against the small resting card and only corrected on the next
   * physical nudge, a visible flick after the grow. CrispHover calls this on
   * every frame it moves the card, naming the BOX the card is heading to
   * (screen px: left/top/width/height): the lean then eases once, from rest
   * to its final pose, in step with the grow, instead of re-targeting every
   * intermediate box (which restarted the card's transform transition every
   * frame). The box stays in force for the host's own pointer samples until
   * the pointer leaves or the engine clears it with `aim(card, null)`.
   * No-op unless the card is actively tilting.
   * @param {HTMLElement} card
   * @param {{x: number, y: number} | null} point screen coordinates, or null to clear the box
   * @param {{left: number, top: number, width: number, height: number} | null} [box]
   */
  function aim(card, point, box = null, blend = 1) {
    const state = cardStates.get(card);
    if (!state) return;
    if (!point) {
      // The engine let go. A pointer still on the card keeps its lean; a card
      // the pointer already left (its leave was deferred to the engine) rests.
      state.aimBox = null;
      state.aimBlend = 1;
      if (!state.active) setNeutral(card);
      return;
    }
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return;
    const named = box && box.width > 0 && box.height > 0 ? box : null;
    if (!state.active) {
      if (state.aimBox) {
        // Held by an engine that is fading the lean out after the pointer
        // left (see doLeave): the lean still moves, the light stays out.
      } else if (named) {
        // An engine naming the box the card is heading to IS the pointer's
        // presence: a card taking over an enlargement from its neighbour sits
        // under that neighbour's box, and the browser's own enter only
        // arrives once the neighbour has shrunk away.
        if (state.leaveTimer != null) {
          clearTimeout(state.leaveTimer);
          state.leaveTimer = null;
        }
        state.active = true;
        card.classList.add('card-tilt-active');
        card.style.setProperty('--card-tilt-opacity', '1');
      } else {
        return;
      }
    }
    state.aimBox = named;
    state.aimBlend = Number.isFinite(blend) ? blend : 1;
    applyTilt(state, { clientX: point.x, clientY: point.y });
  }

  function register(card, options = {}) {
    if (!card) return;
    if (card.closest?.('#player-hand')) {
      // CardHover deliberately owns the fanned hand's pointer geometry, but it
      // drives this module's material variables. Seed the idempotent light
      // layer before declining generic listeners so every authoritative hand
      // rebuild remains a complete 3D object from its first frame.
      ensureGlare(card);
      return;
    }
    if (shouldSkip(card)) return;
    const host = options.host || card;
    const existing = cardStates.get(card);
    if (existing) {
      if (options.preserveExistingHost) return;
      if (existing.host === host && !options.force) return;
      detach(existing);
    }
    attach(host, card, { transparent: options.transparent === true });
  }

  function bindHost(host, card) {
    register(card, { host });
  }

  /* Popup previews are built `card-tilt-disabled` so viewport clamping
     measures a neutral box. The reveal re-arms them — replacing any
     registration the observer made while the class was still on — and from
     then on they answer the FIRST pointer sample they hear, enter or move,
     exactly like an in-place card. A popup opens under the pointer, so any
     hold before it turns is felt as lag; this once waited 250ms, pushed out
     by every sample inside the wait and then for a further 2px move, and on
     every popup surface in the game hovering read as "not 3D rotatable
     instantly, like a 1s delay". The wait served a test oracle (a tilted
     popup's transformed bounding box under-measured its DPR coverage); the
     oracle now measures the art in layout space instead. */
  function registerPreview(card, options = {}) {
    if (!card) return;
    card.classList.remove('card-tilt-disabled');
    register(card, { ...options, force: true });
  }

  /* THE BATTLE POPUP CANNOT HEAR ITS OWN FINGER. It is `pointer-events: none`
     on purpose (game-board.css): it opens over the card it enlarged, and a
     press has to fall through to that card so CardDrag still sees the
     pointerdown that starts a play. Every other enlarged card in the game
     leans and lights under a finger from the moment it lands; this one sat
     still, on the phone, in the one place a card is looked at most.
     So the DOCUMENT hears the finger for it, the card's own box decides
     whether a touch is on it, and everything downstream is the ordinary
     touch path: lit and turned toward the finger on pointerdown, following it
     while it stays down, flat again on lift. Touch only — a mouse roaming the
     page is never "over" a card it cannot hit. The gesture underneath is not
     touched: a press that becomes a drag clears the popup through
     `Cards.clearCardPreview`, which unregisters this. */
  function registerTransparentPreview(card) {
    if (!card) return;
    card.classList.remove('card-tilt-disabled');
    register(card, { host: document, transparent: true, force: true });
  }

  function unregister(card) {
    const state = cardStates.get(card);
    if (!state) return false;
    detach(state);
    cardStates.delete(card);
    return true;
  }

  function scan(root) {
    if (typeof document === 'undefined') return;
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.card').forEach((card) => register(card, { preserveExistingHost: true }));
  }

  function handleMutations(mutations) {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.classList && node.classList.contains('card')) {
          register(node, { preserveExistingHost: true });
        }
        if (node.querySelectorAll) {
          node.querySelectorAll('.card').forEach((card) => register(card, { preserveExistingHost: true }));
        }
      });
    });
  }

  function init() {
    updateReducedMotionPreference();
    if (typeof document === 'undefined') return;
    scan(document);
    if (document.body && typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(handleMutations);
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (typeof window !== 'undefined' && window.matchMedia) {
    const mq = window.matchMedia(REDUCE_MOTION_QUERY);
    if (mq.addEventListener) {
      mq.addEventListener('change', updateReducedMotionPreference);
    } else if (mq.addListener) {
      mq.addListener(updateReducedMotionPreference);
    }
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  return {
    register,
    registerPreview,
    registerTransparentPreview,
    bindHost,
    unregister,
    aim,
    refresh: scan
  };
})();

export { CardTilt };
